'use client'

import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react'
import { ChevronUpDownIcon } from '@heroicons/react/20/solid'
import _ from 'lodash'
import { twMerge } from 'tailwind-merge'

type ListBoxProps = {
  label?: string
  inline?: boolean
  optional?: boolean
  options: { id: number; name: string | any }[] | any[]
  value?: any
  onChange: (val: string | number) => void
} & React.ComponentPropsWithoutRef<'select'>

export default function ListBox(props: ListBoxProps) {
  const { label, optional, inline, options, value, onChange } = props
  const errMsg = _.get(props, 'data-error')
  return (
    <Listbox value={value} onChange={(val) => onChange(val)}>
      <div className={twMerge(inline ? 'flex items-center' : 'w-full')}>
        {label && (
          <span className={twMerge('text-sm/6 mr-2', !inline && 'block mb-1')}>
            {label}
            {optional && <span className='font-light text-xs ml-2'>(optional)</span>}
          </span>
        )}
        <div className='relative grow'>
          <ListboxButton
            className={twMerge(
              'relative cursor-default block w-full rounded border-0 pl-3 pr-10 py-1.5 text-left h-10',
              'ring-1 focus:ring-2 ring-inset ring-zinc-300 dark:ring-zinc-600 placeholder:text-zinc-400 focus:ring-zinc-600',
              'bg-white dark:bg-transparent dark:ring-zinc-500',
            )}
          >
            <span className='block truncate text-sm'>
              {
                _.find(options, (x) => {
                  return x?.id.toString() === value?.toString()
                })?.name
              }
            </span>
            <span className='pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2'>
              <ChevronUpDownIcon aria-hidden='true' className='size-5 text-zinc-400' />
            </span>
          </ListboxButton>
          <ListboxOptions
            transition
            className={twMerge(
              'absolute z-10 mt-1 max-h-120 w-full overflow-auto rounded bg-white dark:bg-zinc-900 py-1 shadow-lg ring-1 ring-zinc-300 dark:ring-zinc-500',
              'focus:outline-hidden data-closed:data-leave:opacity-0 data-leave:transition data-leave:duration-100 data-leave:ease-in',
            )}
          >
            {_.map(options, (o) => (
              <ListboxOption
                key={o.id}
                value={o.id.toString()}
                className='group relative cursor-default select-none py-2 px-4 data-focus:bg-zinc-200 dark:data-focus:bg-zinc-700 text-sm'
              >
                <span className='block truncate font-normal group-data-selected:font-semibold'>{o.name}</span>
              </ListboxOption>
            ))}
          </ListboxOptions>
        </div>
        {!inline && <div className='h-3 py-1 pl-3 text-xs text-red-800 dark:text-orange-500'>{errMsg}</div>}
      </div>
    </Listbox>
  )
}
